/**
 * ClinicalProfileEdit.jsx — M16B-03
 * ================================
 * Patient-facing editor for the canonical Clinical Profile structured
 * fields.  Persists via:  PATCH /api/patient/clinical-profile/{patient_id}
 *
 * Reuses existing form patterns (no new design system).  Reads the
 * current shape via the M16B-02 read aggregator before each edit so
 * the form always reflects the live source-of-truth.
 *
 * Out of scope (per M16B-03 brief):
 *   - No attestation UI
 *   - No clinical review / risk engine
 *   - No doctor sign-off
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Save, Plus, Trash2, ArrowLeft, RefreshCw, ClipboardList, CheckCircle2, Plane,
} from 'lucide-react';
import { api } from '../../utils/api';
import { PersonalInformationCard } from '../../components/patient/PersonalInformationCard';
import { timezoneForAirport, localToUtcIso } from '../../utils/airportTimezones';
// TREATX-UAT-RELEASE-FIX-SEQUENCE-01 / Phase 1 — shared clinical
// validators (year / height / weight / whitespace / date order).
// Reused so onboarding / edit / settings / admin all behave identically.
import {
  normalizeYearInput,
  validateYear,
  normalizeWhitespaceOnSave,
  isYearColumn,
  CURRENT_YEAR,
  MIN_YEAR,
} from '../../utils/clinicalFieldValidators';
// TREATX-UAT-PHONE-BULK-MIGRATION-02 — canonical E.164 phone input.
// Applied to the emergency_contacts.phone column via isPhoneColumn().
import { PhoneInput } from '../../components/ui/phone-input';

// Detect a phone column in the generic list renderer. Kept inline (not
// in the validator module) because it is a render-time concern, not a
// validation rule.
function isPhoneColumn(col) {
  if (!col) return false;
  const id = String(col.id || '').toLowerCase();
  if (id === 'phone' || id.endsWith('_phone') || id === 'mobile' || id === 'telephone') return true;
  const label = String(col.label || '').toLowerCase();
  return label.startsWith('phone ') || label === 'phone' || label.includes('(phone');
}

const FIELD_DEFS = [
  { id: 'conditions',         kind: 'list',  labelKey: 'patient.clinicalProfile.sectionRow_condition',  helpKey: 'patient.clinicalProfile.conditionsSectionSub',
    columns: [
      { id: 'name',   label: 'Condition' },
      { id: 'status', label: 'Status (active / managed / resolved)' },
      { id: 'since',  label: 'Since (year)' },
      { id: 'notes',  label: 'Notes (optional)' },
    ] },
  { id: 'medications',        kind: 'list',  labelKey: 'patient.clinicalProfile.sectionRow_medications', helpKey: 'patient.clinicalProfile.medicationsSectionSub',
    columns: [
      { id: 'name',      label: 'Medicine name' },
      { id: 'dose',      label: 'Dose (e.g., 10mg)' },
      { id: 'frequency', label: 'How often (e.g., daily)' },
      { id: 'notes',     label: 'Notes (optional)' },
    ] },
  { id: 'allergies',          kind: 'list',  labelKey: 'patient.clinicalProfile.sectionRow_allergies',  helpKey: 'patient.clinicalProfile.allergiesSectionSub',
    columns: [
      { id: 'substance', label: 'Substance' },
      { id: 'severity',  label: 'Severity (mild / medium / high)' },
      { id: 'reaction',  label: 'Reaction (rash, hives, …)' },
      { id: 'notes',     label: 'Notes (optional)' },
    ] },
  { id: 'past_surgeries',     kind: 'list',  labelKey: 'patient.clinicalProfile.sectionRow_surgeries', helpKey: 'patient.clinicalProfile.surgeriesSectionSub',
    columns: [
      { id: 'procedure', label: 'Procedure' },
      { id: 'year',      label: 'Year' },
      { id: 'hospital',  label: 'Hospital' },
      { id: 'notes',     label: 'Notes (optional)' },
    ] },
  { id: 'past_procedures',    kind: 'list',  labelKey: 'patient.clinicalProfile.surgeriesSection', helpKey: 'patient.clinicalProfile.surgeriesSectionSub',
    columns: [
      { id: 'procedure', label: 'Procedure' },
      { id: 'year',      label: 'Year' },
      { id: 'hospital',  label: 'Hospital' },
      { id: 'notes',     label: 'Notes (optional)' },
    ] },
  { id: 'family_history',     kind: 'list',  labelKey: 'patient.clinicalProfile.conditionsSection', helpKey: 'patient.clinicalProfile.conditionsSectionSub',
    columns: [
      { id: 'relation',  label: 'Relation (mother, father, sibling, …)' },
      { id: 'condition', label: 'Condition' },
      { id: 'notes',     label: 'Notes (optional)' },
    ] },
  { id: 'emergency_contacts', kind: 'list',  labelKey: 'patient.clinicalProfile.sectionRow_emergency', helpKey: 'patient.clinicalProfile.emergencyContactSub',
    columns: [
      { id: 'name',         label: 'Full name' },
      { id: 'phone',        label: 'Phone (with country code)' },
      { id: 'relationship', label: 'Relationship' },
      { id: 'notes',        label: 'Notes (optional)' },
    ] },
  { id: 'lifestyle',          kind: 'dict',  labelKey: 'patient.clinicalProfile.lifestyleSection', helpKey: 'patient.clinicalProfile.lifestyleSection',
    columns: [
      { id: 'smoking_status', label: 'Smoking (non-smoker / former / current)' },
      { id: 'alcohol',        label: 'Alcohol (none / social / regular)' },
      { id: 'exercise',       label: 'Exercise (rarely / weekly / daily)' },
      { id: 'diet',           label: 'Diet (omnivore / vegetarian / vegan / other)' },
      { id: 'notes',          label: 'Notes (optional)' },
    ] },
  { id: 'travel_information', kind: 'travel', labelKey: 'patient.clinicalProfile.sectionRow_travel', helpKey: 'patient.clinicalProfile.travelHelp',
    columns: [] },
  { id: 'medical_notes',      kind: 'str',   labelKey: 'patient.intake.notes', helpKey: 'patient.intake.notes',
    columns: [{ id: 'notes', label: 'Notes' }] },
];

function emptyRow(cols) {
  const r = {};
  cols.forEach((c) => { r[c.id || c] = ''; });
  return r;
}

/**
 * TravelLeg — M16B-03D normalized datetime block for one leg
 * (arrival or departure). Stores: airport / flight / datetime_local /
 * timezone / datetime_utc. Patient enters airport + local datetime;
 * timezone is derived from the airport; UTC is computed client-side.
 */
function TravelLeg({ leg, value, onChange }) {
  const { t } = useTranslation();
  const v = value || {};
  const tz = v.timezone || timezoneForAirport(v.airport);
  const update = (patch) => {
    const merged = { ...v, ...patch };
    // Re-derive timezone if airport changed
    if (Object.prototype.hasOwnProperty.call(patch, 'airport')) {
      merged.timezone = timezoneForAirport(merged.airport) || null;
    }
    // Re-derive UTC whenever local or timezone changes
    if (
      Object.prototype.hasOwnProperty.call(patch, 'datetime_local')
      || Object.prototype.hasOwnProperty.call(patch, 'airport')
    ) {
      merged.datetime_utc = merged.datetime_local
        ? localToUtcIso(merged.datetime_local, merged.timezone)
        : null;
    }
    onChange(merged);
  };
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50" data-testid={`cp-travel-${leg}`}>
      <div className="flex items-center gap-2 mb-2">
        <Plane className="h-3.5 w-3.5 text-slate-500" />
        <h3 className="text-[12.5px] font-semibold text-slate-800">
          {leg === 'arrival' ? 'Arrival leg' : 'Departure leg'}
        </h3>
      </div>
      <div className="grid grid-cols-12 gap-2">
        <label className="col-span-3 text-[11px] text-slate-600">
          Airport (IATA)
          <input
            type="text"
            maxLength={3}
            placeholder={t('patient.clinicalProfile.airportCodePlaceholder')}
            className="mt-1 w-full px-2 py-1.5 border border-slate-200 rounded text-[12.5px] uppercase"
            value={v.airport || ''}
            onChange={(e) => update({ airport: e.target.value.toUpperCase().slice(0, 3) })}
            data-testid={`cp-travel-${leg}-airport`}
          />
        </label>
        <label className="col-span-3 text-[11px] text-slate-600">
          Flight number
          <input
            type="text"
            placeholder={t('patient.clinicalProfile.flightCodePlaceholder')}
            className="mt-1 w-full px-2 py-1.5 border border-slate-200 rounded text-[12.5px]"
            value={v.flight || ''}
            onChange={(e) => update({ flight: e.target.value })}
            data-testid={`cp-travel-${leg}-flight`}
          />
        </label>
        <label className="col-span-6 text-[11px] text-slate-600">
          Local date &amp; time at {leg === 'arrival' ? 'arrival airport' : 'departure airport'}
          <input
            type="datetime-local"
            className="mt-1 w-full px-2 py-1.5 border border-slate-200 rounded text-[12.5px]"
            value={v.datetime_local || ''}
            onChange={(e) => update({ datetime_local: e.target.value })}
            data-testid={`cp-travel-${leg}-datetime`}
          />
        </label>
      </div>
      <div className="mt-2 text-[11px] text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`cp-travel-${leg}-derived`}>
        <span>
          Timezone:{' '}
          <span className="font-mono text-slate-700" data-testid={`cp-travel-${leg}-tz`}>
            {tz || (v.airport ? 'unknown — UTC fallback' : '—')}
          </span>
        </span>
        <span>
          UTC:{' '}
          <span className="font-mono text-slate-700" data-testid={`cp-travel-${leg}-utc`}>
            {v.datetime_utc || '—'}
          </span>
        </span>
      </div>
    </div>
  );
}

export default function ClinicalProfileEdit() {
  const { t } = useTranslation();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tier, setTier] = useState(null);
  const [revNo, setRevNo] = useState(null);
  const [dobEligibility, setDobEligibility] = useState(null); // eslint-disable-line no-unused-vars
  const [fields, setFields] = useState(() => {
    const blank = {};
    FIELD_DEFS.forEach((f) => {
      if (f.kind === 'list') blank[f.id] = [];
      else if (f.kind === 'dict') blank[f.id] = {};
      else blank[f.id] = '';
    });
    return blank;
  });

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Resolve patient identity from localStorage (set by the unified
      // login flow).  Avoid /auth/patient/me — that endpoint rejects the
      // unified JWT with "Invalid token type" and the 401 would force a
      // logout via the global interceptor.
      let pid = null;
      let meUser = null;
      try {
        const raw = localStorage.getItem('treatx_unified_user')
                 || localStorage.getItem('treatx_user');
        if (raw) {
          meUser = JSON.parse(raw);
          pid = meUser?.person_id || meUser?.user_id || meUser?.id || meUser?.sub;
        }
      } catch { /* ignore parse failure */ }
      if (!pid) {
        // Fallback to /auth/patient/me only when storage is empty
        try {
          const { data: m } = await api.get('/auth/patient/me');
          meUser = m;
          pid = m?.person_id || m?.user_id || m?.id;
        } catch { /* logged-out — will surface as error below */ }
      }
      if (!pid) throw new Error('No patient identity in session');
      setMe({ ...(meUser || {}), patient_id: pid });
      const { data } = await api.get(`/patient/clinical-profile/${pid}`);
      const p = data?.profile || {};
      const next = { ...fields };
      // Hydrate from the canonical structured fields (overrides legacy)
      // The read aggregator emits them at the top level when overlay present.
      ['conditions', 'medications', 'allergies', 'past_surgeries',
       'past_procedures', 'family_history', 'emergency_contacts'
      ].forEach((k) => {
        const v = p[k];
        next[k] = Array.isArray(v) ? v : [];
      });
      next.lifestyle = (p.lifestyle && typeof p.lifestyle === 'object') ? p.lifestyle : {};
      next.travel_information = (p.travel_information && typeof p.travel_information === 'object') ? p.travel_information : {};
      next.medical_notes = typeof p.medical_notes === 'string' ? p.medical_notes : '';
      // surgeries_or_procedures is a legacy union field — we leave the
      // structured arrays alone if the canonical row is present
      setFields(next);
      setTier(p.profile_completeness || null);
      setRevNo(p?._meta?.canonical_rev_no ?? null);
    } catch (e) {
      setError('Could not load your clinical profile');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  // P0-DOB-SELF-DECLARATION-01 — read the canonical eligibility (never the
  // raw DoB) so the section can show either "on file" or the capture form.
  useEffect(() => {
    let active = true;
    api.get('/patient/profile/eligibility')
      .then(({ data }) => { if (active) setDobEligibility(data); })
      .catch(() => { if (active) setDobEligibility({ eligible: true }); });
    return () => { active = false; };
  }, []);

  // TREATX-UAT-RELEASE-FIX-SEQUENCE-01 / Phase 1 — Compute a flat list of
  // year-column violations across all list-kind fields so we can disable
  // Save and surface a single banner explaining why.
  const yearValidationErrors = useMemo(() => {
    const errs = [];
    FIELD_DEFS.forEach((f) => {
      if (f.kind !== 'list') return;
      const yearCols = (f.columns || []).filter((c) => isYearColumn(c));
      if (yearCols.length === 0) return;
      const rows = Array.isArray(fields[f.id]) ? fields[f.id] : [];
      rows.forEach((row, rIdx) => {
        yearCols.forEach((c) => {
          const r = validateYear(row?.[c.id] ?? '');
          if (!r.valid) errs.push({ field: f.id, row: rIdx, col: c.id, error: r.error });
        });
      });
    });
    return errs;
  }, [fields]);
  const hasYearErrors = yearValidationErrors.length > 0;

  const submit = useCallback(async () => {
    if (!me) return;
    // Phase 1 — block save if any year field is invalid
    if (yearValidationErrors.length > 0) {
      setError(
        `Please fix ${yearValidationErrors.length} year field error${
          yearValidationErrors.length === 1 ? '' : 's'
        } before saving.`,
      );
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    // Phase 1 — whitespace normalization on save (does not mutate
    // editor state). Trims and collapses runs of inner whitespace for
    // every string value in list rows / dict fields / the free-text
    // medical_notes block.
    const cleanedFields = (() => {
      const cleanString = (v) => (typeof v === 'string' ? normalizeWhitespaceOnSave(v) : v);
      const next = { ...fields };
      FIELD_DEFS.forEach((f) => {
        if (f.kind === 'list') {
          const rows = Array.isArray(next[f.id]) ? next[f.id] : [];
          next[f.id] = rows.map((row) => {
            if (!row || typeof row !== 'object') return row;
            const cleaned = {};
            Object.keys(row).forEach((k) => { cleaned[k] = cleanString(row[k]); });
            return cleaned;
          });
        } else if (f.kind === 'dict') {
          const d = next[f.id] || {};
          const cleaned = {};
          Object.keys(d).forEach((k) => { cleaned[k] = cleanString(d[k]); });
          next[f.id] = cleaned;
        } else if (f.kind === 'str') {
          next[f.id] = cleanString(next[f.id]);
        }
      });
      return next;
    })();
    try {
      const { data } = await api.patch(`/patient/clinical-profile/${me.patient_id}`, {
        fields: cleanedFields,
        reason: 'Patient self-update via Clinical Profile editor',
      });
      if (data?.success) {
        setSuccess(
          data.noop
            ? 'No effective change — nothing to save.'
            : `Saved · revision #${data.rev_no} · tier=${data?.tier?.tier}`,
        );
        setTier(data.tier || null);
        setRevNo(data.rev_no || null);
        // Reflect normalized values back into state so the user sees
        // the same shape the backend will return on next reload.
        setFields(cleanedFields);
      } else {
        throw new Error(data?.message || 'Save failed');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [me, fields, yearValidationErrors]);

  const updateListRow = (fid, idx, key, val) => {
    setFields((prev) => {
      const arr = Array.isArray(prev[fid]) ? [...prev[fid]] : [];
      arr[idx] = { ...(arr[idx] || {}), [key]: val };
      return { ...prev, [fid]: arr };
    });
  };
  // TREATX-UAT-RELEASE-FIX-SEQUENCE-01 / Phase 1 — year-column variant
  // that strips non-digits and caps at 4 chars before storing. Keeps
  // the rest of the editor untouched.
  const updateListRowYear = (fid, idx, key, val) => {
    const clean = normalizeYearInput(val);
    updateListRow(fid, idx, key, clean);
  };
  const addListRow = (fid, cols) => {
    setFields((prev) => ({
      ...prev,
      [fid]: [...(Array.isArray(prev[fid]) ? prev[fid] : []), emptyRow(cols)],
    }));
  };
  const removeListRow = (fid, idx) => {
    setFields((prev) => {
      const arr = Array.isArray(prev[fid]) ? [...prev[fid]] : [];
      arr.splice(idx, 1);
      return { ...prev, [fid]: arr };
    });
  };
  const updateDictField = (fid, key, val) => {
    setFields((prev) => ({ ...prev, [fid]: { ...(prev[fid] || {}), [key]: val } }));
  };

  const tierBadge = useMemo(() => {
    const t = tier?.tier || 'minimum';
    const tone = {
      minimum: 'bg-slate-100 text-slate-700 ring-slate-200',
      recommended: 'bg-amber-100 text-amber-800 ring-amber-200',
      complete: 'bg-sky-100 text-sky-800 ring-sky-200',
      treatment_ready_candidate: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
    }[t] || 'bg-slate-100 text-slate-700 ring-slate-200';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${tone}`} data-testid="cp-tier-badge">
        Tier · {t}
      </span>
    );
  }, [tier]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="clinical-profile-edit">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <a href="/#/patient/dashboard" className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 text-sm" data-testid="cp-back">
              <ArrowLeft className="h-4 w-4" /> {t('patient.shell.goToDashboard')}
            </a>
          </div>
          <button
            type="button"
            onClick={fetchProfile}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-slate-200 hover:bg-slate-50 text-slate-700"
            data-testid="cp-refresh"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> {t('patient.common.refresh')}
          </button>
        </div>

        <header className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-4">
          <div className="flex items-start gap-3">
            <ClipboardList className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h1 className="text-base font-semibold text-slate-900" data-testid="cp-title">{t('patient.clinicalProfile.title')}</h1>
              <p className="text-[12px] text-slate-500 mt-0.5">
                {t('patient.clinicalProfile.pageSubtitle')}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {tierBadge}
                {revNo != null && (
                  <span className="text-[11px] text-slate-500" data-testid="cp-rev-no">#{revNo}</span>
                )}
              </div>
            </div>
          </div>
        </header>

        {loading && (
          <div className="text-center text-slate-500 py-10" data-testid="cp-loading">{t('patient.common.loading')}</div>
        )}
        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2 rounded mb-3 text-sm" data-testid="cp-error">{error}</div>
        )}
        {success && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-2 rounded mb-3 text-sm inline-flex items-center gap-2" data-testid="cp-success">
            <CheckCircle2 className="h-4 w-4" /> {success}
          </div>
        )}

        {/* P0-DOB-ONBOARDING-01 — Personal information. Projects the
            canonical identity record; the canonical date of birth
            (`persons.date_of_birth`) is captured through the SAME endpoint
            and age authority as every other entry point, so there is no
            second field and no second identity row. */}
        {!loading && <PersonalInformationCard />}

        {!loading && FIELD_DEFS.map((f) => (
          <section
            key={f.id}
            className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-3"
            data-testid={`cp-section-${f.id}`}
          >
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-semibold text-slate-900">{t(f.labelKey)}</h2>
              {f.kind === 'list' && (
                <button
                  type="button"
                  onClick={() => addListRow(f.id, f.columns)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid={`cp-add-${f.id}`}
                >
                  <Plus className="h-3 w-3" /> {t('patient.clinicalProfile.add')}
                </button>
              )}
            </div>
            {f.helpKey && (
              <p className="text-[11.5px] text-slate-500 mb-3" data-testid={`cp-help-${f.id}`}>{t(f.helpKey)}</p>
            )}

            {f.kind === 'list' && (
              <div className="space-y-3">
                {(fields[f.id] || []).length === 0 && (
                  <div className="text-[12px] text-slate-500 italic">{t('patient.clinicalProfile.noneAdded')}</div>
                )}
                {(fields[f.id] || []).map((row, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`cp-row-${f.id}-${idx}`}>
                    {f.columns.map((col) => {
                      const isYear = isYearColumn(col);
                      const isPhone = !isYear && isPhoneColumn(col);
                      const rawValue = row[col.id] ?? '';
                      const yearError = isYear ? validateYear(rawValue) : null;
                      return (
                      <label key={col.id} className={`col-span-${Math.max(3, Math.floor(11 / f.columns.length))} text-[11px] text-slate-600`}>
                        {col.label}
                        {isYear ? (
                          <>
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="\d{4}"
                              maxLength={4}
                              placeholder={`YYYY (${MIN_YEAR}-${CURRENT_YEAR})`}
                              className={`mt-1 w-full px-2 py-1.5 border rounded text-[12.5px] ${
                                yearError && !yearError.valid
                                  ? 'border-rose-400 bg-rose-50/40'
                                  : 'border-slate-200'
                              }`}
                              value={rawValue}
                              onChange={(e) => updateListRowYear(f.id, idx, col.id, e.target.value)}
                              data-testid={`cp-input-${f.id}-${idx}-${col.id}`}
                              aria-invalid={!!(yearError && !yearError.valid)}
                            />
                            {yearError && !yearError.valid && (
                              <span
                                className="block mt-0.5 text-[10.5px] text-rose-600"
                                data-testid={`cp-input-${f.id}-${idx}-${col.id}-error`}
                              >
                                {yearError.error}
                              </span>
                            )}
                          </>
                        ) : isPhone ? (
                          <div className="mt-1">
                            <PhoneInput
                              value={rawValue}
                              onChange={(e164) => updateListRow(f.id, idx, col.id, e164)}
                              testId={`cp-input-${f.id}-${idx}-${col.id}`}
                              placeholder={t('patient.clinicalProfile.phonePlaceholder')}
                            />
                          </div>
                        ) : (
                          <input
                            className="mt-1 w-full px-2 py-1.5 border border-slate-200 rounded text-[12.5px]"
                            value={rawValue}
                            onChange={(e) => updateListRow(f.id, idx, col.id, e.target.value)}
                            data-testid={`cp-input-${f.id}-${idx}-${col.id}`}
                          />
                        )}
                      </label>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => removeListRow(f.id, idx)}
                      className="col-span-1 text-slate-400 hover:text-rose-600 justify-self-end pb-1.5"
                      data-testid={`cp-remove-${f.id}-${idx}`}
                      title={t('patient.clinicalProfile.removeRow')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {f.kind === 'dict' && (
              <div className="grid grid-cols-2 gap-3">
                {f.columns.map((col) => (
                  <label key={col.id} className="text-[12px] text-slate-600">
                    {col.label}
                    <input
                      className="mt-1 w-full px-2 py-1.5 border border-slate-200 rounded text-[12.5px]"
                      value={fields[f.id]?.[col.id] ?? ''}
                      onChange={(e) => updateDictField(f.id, col.id, e.target.value)}
                      data-testid={`cp-input-${f.id}-${col.id}`}
                    />
                  </label>
                ))}
              </div>
            )}

            {f.kind === 'str' && (
              <textarea
                rows={3}
                className="w-full px-2 py-1.5 border border-slate-200 rounded text-[12.5px]"
                value={fields[f.id] || ''}
                onChange={(e) => setFields((prev) => ({ ...prev, [f.id]: e.target.value }))}
                data-testid={`cp-input-${f.id}`}
              />
            )}

            {f.kind === 'travel' && (
              <div className="space-y-3">
                <TravelLeg
                  leg="arrival"
                  value={fields[f.id]?.arrival}
                  onChange={(next) => setFields((prev) => ({
                    ...prev,
                    [f.id]: { ...(prev[f.id] || {}), arrival: next },
                  }))}
                />
                <TravelLeg
                  leg="departure"
                  value={fields[f.id]?.departure}
                  onChange={(next) => setFields((prev) => ({
                    ...prev,
                    [f.id]: { ...(prev[f.id] || {}), departure: next },
                  }))}
                />
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-[12px] text-slate-600">
                    Hotel
                    <input
                      type="text"
                      className="mt-1 w-full px-2 py-1.5 border border-slate-200 rounded text-[12.5px]"
                      value={fields[f.id]?.hotel_name ?? ''}
                      onChange={(e) => setFields((prev) => ({
                        ...prev,
                        [f.id]: { ...(prev[f.id] || {}), hotel_name: e.target.value },
                      }))}
                      data-testid="cp-travel-hotel"
                    />
                  </label>
                  <label className="text-[12px] text-slate-600">
                    Notes (optional)
                    <input
                      type="text"
                      className="mt-1 w-full px-2 py-1.5 border border-slate-200 rounded text-[12.5px]"
                      value={fields[f.id]?.notes ?? ''}
                      onChange={(e) => setFields((prev) => ({
                        ...prev,
                        [f.id]: { ...(prev[f.id] || {}), notes: e.target.value },
                      }))}
                      data-testid="cp-travel-notes"
                    />
                  </label>
                </div>
                <p className="text-[11px] text-slate-500" data-testid="cp-travel-storage-note">
                  STORE = UTC · DISPLAY = LOCAL — your local date/time is preserved exactly; the UTC equivalent is stored alongside so your clinic sees the correct moment regardless of their timezone.
                </p>
              </div>
            )}
          </section>
        ))}

        <div className="sticky bottom-3 bg-white border border-slate-200 rounded-xl shadow-md px-4 py-3 flex items-center justify-between" data-testid="cp-save-bar">
          <span className="text-[11.5px] text-slate-500">
            {hasYearErrors
              ? `${yearValidationErrors.length} year field error${yearValidationErrors.length === 1 ? '' : 's'} — fix before saving.`
              : t('patient.clinicalProfile.saveHint')}
          </span>
          <button
            type="button"
            onClick={submit}
            disabled={saving || loading || hasYearErrors}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-[12.5px] font-semibold"
            data-testid="cp-save-btn"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? t('patient.clinicalProfile.saving') : t('patient.clinicalProfile.saveProfile')}
          </button>
        </div>
      </div>
    </div>
  );
}
